/**
 * INCENTIVE-TRACKER MODULE
 * Self-contained. Import only from /shared or within this module.
 *
 * Single source of truth for the lifecycle + enums. The DB enforces the state
 * machine (migration 0041 triggers); this mirrors it for the UI so we can label
 * statuses and decide which action button to show. The DB is the real gate —
 * if these drift, Postgres still says no.
 */

// Lifecycle states (must match the incentive_status enum).
export const STATUS = {
  DRAFT: "draft",
  PENDING_REVIEW: "pending_review",
  APPROVED: "approved",
  SENT: "sent",
  ACTIVE: "active",
  SCORING: "scoring",
  COMPLETED: "completed",
};

export const STATUS_ORDER = [
  STATUS.DRAFT,
  STATUS.PENDING_REVIEW,
  STATUS.APPROVED,
  STATUS.SENT,
  STATUS.ACTIVE,
  STATUS.SCORING,
  STATUS.COMPLETED,
];

export const STATUS_LABEL = {
  draft: "Draft",
  pending_review: "Pending review",
  approved: "Approved",
  sent: "Sent",
  active: "Active",
  scoring: "Scoring",
  completed: "Completed",
};

// Legal transitions (mirror of incentive_program_status_guard). draft has two
// exits: → pending_review when the company requires review, else → sent.
export const TRANSITIONS = {
  draft: ["pending_review", "sent"],
  pending_review: ["approved", "draft"],
  approved: ["sent"],
  sent: ["active"],
  active: ["scoring"],
  scoring: ["completed"],
  completed: [],
};

export function canTransition(from, to) {
  return (TRANSITIONS[from] || []).includes(to);
}

// Metric types (must match incentive_metric_type). `auto` marks the metrics the
// resolver can pull from the tenant's depletion reports — see metricResolver.js,
// which is the authority; this flag only drives labels and hints.
export const METRIC_TYPES = [
  { value: "new_pods", label: "New points of distribution", auto: true },
  { value: "cases", label: "Cases sold", auto: true },
  { value: "depletions", label: "Depletions", auto: true },
  { value: "other", label: "Other", auto: false },
  { value: "custom", label: "Custom (free text)", auto: false },
];

// What the resolver measures for each automatic metric, in the user's words.
export const METRIC_AUTO_HINT = {
  new_pods: "Counts accounts that sold during the window and hadn't sold in the 12 months before it.",
  cases: "Sums case sales for this distributor across the program window.",
  depletions: "Sums depletion volume for this distributor across the program window.",
};

export const METRIC_LABEL = Object.fromEntries(METRIC_TYPES.map((m) => [m.value, m.label]));

// A reasonable default unit per metric (editable by the user).
export const METRIC_DEFAULT_UNIT = {
  new_pods: "pods",
  cases: "cases",
  depletions: "cases",
  other: "units",
  custom: "",
};

// A "custom" goal is written entirely as free text (no structured target).
export const METRIC_CUSTOM = "custom";

// Payout structures (must match incentive_payout_type).
export const PAYOUT_TYPES = [
  { value: "flat", label: "Flat — fixed payout if target met" },
  { value: "per_unit", label: "Per unit — amount × units achieved" },
  { value: "tiered", label: "Tiered — payout by threshold reached" },
  { value: "custom", label: "Custom — describe in text" },
];

export const PAYOUT_LABEL = Object.fromEntries(PAYOUT_TYPES.map((p) => [p.value, p.label]));

// A "custom" payout is described in free text and entered manually at scoring
// time (it can't be computed from a formula).
export const PAYOUT_CUSTOM = "custom";

export const DATA_SOURCE = { SYSTEM: "system", MANUAL: "manual_upload" };
